import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import CmSvgIcon from '@/components/cm-svg-icon/cm-svg-icon.vue'

import { ref, reactive, computed, h, nextTick, watch } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import { TerminalViewService, TerminalItem, TerminalQueryParams } from '../terminal-view/terminal-view.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage, ElMessageBox } from 'element-plus'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { TableOpt } from '@/components/cmTable/types'

export type TerminalListInitialQueryPatch = {
  key_word?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'terminal-list-top10',
  props: {
    viewMode: { default: TableViewMode.preview },
    initialQueryPatch: { default: undefined }
  },
  emits: ["viewAll", "detail"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n()

const props = __props

const emit = __emit

const injector = useReflectiveInjector([TerminalViewService])
const terminalService = injector.get(TerminalViewService)
const tableRef = ref<any>(null)

const { refreshList } = useCmTableListRefresh(tableRef)
/** 与接口字段一致；否则 CmTable 写入 page_num，请求仍带 queryParams 里写死的 page */
const pageOption = { pageNumName: 'page', pageSizeName: 'limit' }

const form = reactive<TerminalQueryParams>({
  key_word: '',
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  sort: '',
  asc: false,
  useMemory: false
})
const formColumns = reactive<Record<string, never>>({})

watch(
  () => props.initialQueryPatch,
  (patch) => {
    if (props.viewMode !== TableViewMode.full) return
    if (patch == null) {
      form.key_word = ''
      form.page = 1
      nextTick(() => void refreshList())
      return
    }
    form.key_word = patch.key_word ?? ''
    form.page = 1
    nextTick(() => void refreshList())
  },
  { deep: true, immediate: true },
)

watch(
  () => props.viewMode,
  (vm) => {
    form.limit = vm === TableViewMode.preview ? 10 : 20
  }
)

const selectedTerminals = ref<TerminalItem[]>([])

const selectedCount = computed(() => selectedTerminals.value.length)

const tableOpt = computed<TableOpt>(() => ({
  bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : undefined,
  columns: [
    { prop: 'computer_name', label: t('bootOptimize.computerName') },
    { prop: 'user_mark', label: t('bootOptimize.registerUser') },
    { prop: 'ip_address', label: t('bootOptimize.ipAddress') },
    { prop: 'login_status', label: t('bootOptimize.terminalStatus') },
    { prop: 'active_count', label: t('bootOptimize.activeBootItems'), sortable: 'custom' },
    { prop: 'not_optimized_count', label: t('bootOptimize.optimizableItems'), sortable: 'custom' },
    { prop: 'optimized_count', label: t('bootOptimize.optimizedItems'), sortable: 'custom' }
  ],
  selection: true,
  usePagination: false,
  showSearch: false,
  showForm: false,
  showActionBar: false,
  showRightToolbar: true,
  rowKey: 'device_id',
  path: 'list',
  totalPath: 'total',
  height: props.viewMode === TableViewMode.preview ? undefined : '100%',
  disableScrollLoad: props.viewMode === TableViewMode.preview,
  hiddenPageTool: props.viewMode === TableViewMode.preview
}))

function getListApi(params: any) {
  const query: TerminalQueryParams = {
    key_word: params.key_word || '',
    page: params.page || 1,
    limit: params.limit || (props.viewMode === TableViewMode.preview ? 10 : 20),
    sort: params.sort || '',
    asc: params.asc || false,
    useMemory: false
  }
  return terminalService.getListByServer(query)
}

async function handleSearch(value: string) {
  form.key_word = value
  await refreshList()
}

async function handleQuery() {
  await refreshList()
}

function handleReset() {
  form.key_word = ''
  form.page = 1
  form.sort = ''
  form.asc = false
  void refreshList()
}

function handleViewAll() {
  emit('viewAll')
}

function handleSelectionChange(selection: TerminalItem[], _ids: string[]) {
  selectedTerminals.value = selection
}

function openDrawer(row: TerminalItem, optimized: number) {
  emit('detail', { row, optimized })
}

function scanReport() {
  ElMessageBox({
    title: t('cmtable.tip'),
    showCancelButton: true,
    message: h('div', { style: 'display:flex;align-items:center;margin-left:10px;margin-top:25px;margin-bottom:20px' }, [
      h(CmSvgIcon, { name: 'views/dashboard/manage/boot-optimize/_components/_images/client_modal_tip', size: 20, monochrome: true }),
      h('div', { style: 'margin-left:10px' }, [
        h('p', null, t('bootOptimize.scanReportLine1', { count: selectedCount.value })),
        h('p', null, t('bootOptimize.scanReportLine2'))
      ])
    ]),
    confirmButtonText: t('common.confirm'),
    cancelButtonText: t('common.cancel'),
  }).then(() => {
    const ids = selectedTerminals.value.map(item => item.device_id)
    terminalService.scanReport({ execute_type: 0, device_list: ids, group_id: -1 }).then(() => {
      ElMessage.success(t('bootOptimize.scanCmdSuccess', { count: ids.length }))
      tableRef.value?.tableRef?.clearSelection()
      selectedTerminals.value = []
    }).catch(() => {
      ElMessage.error(t('bootOptimize.scanCmdFailed', { count: ids.length }))
      tableRef.value?.tableRef?.clearSelection()
      selectedTerminals.value = []
    })
  })
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    "table-opt": tableOpt.value,
    api: getListApi,
    modelValue: form,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form) = $event)),
    "page-option": pageOption,
    "first-loading": true,
    onChangeSelected: handleSelectionChange
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: form,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form) = $event)),
        "form-columns": formColumns,
        "show-search": true,
        "search-placeholder": _ctx.$t('bootOptimize.searchPlaceholderTerminal'),
        "popover-threshold": 0,
        onSearch: handleSearch,
        onQuery: handleQuery,
        onReset: handleReset
      }, {
        actions: _withCtx(() => [
          _createVNode(_component_el_button, {
            disabled: !selectedCount.value,
            type: "primary",
            size: "small",
            onClick: scanReport
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('bootOptimize.scanReport')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          (_ctx.viewMode === _unref(TableViewMode).preview)
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "primary",
                size: "small",
                plain: "",
                onClick: handleViewAll
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "form-columns", "search-placeholder"])
    ]),
    computer_name: _withCtx(({ row }: any) => [
      _createVNode(TerminalIconName, {
        computer_name: (row as TerminalItem).computer_name,
        system_type: (row as TerminalItem).system_type,
        login_status: (row as TerminalItem).login_status
      }, null, 8, ["computer_name", "system_type", "login_status"])
    ]),
    ip_address: _withCtx(({ row }: any) => [
      _createVNode(IPColumn, {
        cip: (row as TerminalItem).client_ip,
        sip: (row as TerminalItem).system_ip
      }, null, 8, ["cip", "sip"])
    ]),
    login_status: _withCtx(({ row }: any) => [
      _createTextVNode(_toDisplayString((row as TerminalItem).login_status == 1 ? _ctx.$t('bootOptimize.online') : _ctx.$t('bootOptimize.offline')), 1)
    ]),
    active_count: _withCtx(({ row,value }: any) => [
      _createVNode(_component_el_button, {
        onClick: ($event: any) => (openDrawer(row as TerminalItem, 0)),
        disabled: !value,
        type: "text",
        size: "small"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(value), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"])
    ]),
    not_optimized_count: _withCtx(({ row,value }: any) => [
      _createVNode(_component_el_button, {
        onClick: ($event: any) => (openDrawer(row as TerminalItem, 2)),
        disabled: !value,
        type: "text",
        size: "small"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(value), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"])
    ]),
    optimized_count: _withCtx(({ value,row }: any) => [
      _createVNode(_component_el_button, {
        onClick: ($event: any) => (openDrawer(row as TerminalItem, 1)),
        disabled: !value,
        type: "text",
        size: "small"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(value), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"])
    ]),
    _: 1
  }, 8, ["table-opt", "modelValue"]))
}
}

})